import {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router";
import { ToastContainer, toast } from 'react-toastify';
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs.tsx";
import { Crumb} from "../../components/Breadcrumbs/Breadcrumbs.tsx";
import CustomButton from "../../components/CustomButton/CustomButton.tsx";
import Checkbox from "../../components/Checkbox/Checkbox.tsx";
import CustomModal from "../../components/CustomModal/CustomModal.tsx";
import iconPen from "url:../../assets/icons/pencil-bare.svg";
import iconTrash from "url:../../assets/icons/trash.svg";
import iconLocked from "url:../../assets/icons/locked.svg";
import iconAi from "url:../../assets/icons/ai.svg";
import Api from "../../services/Api.ts";
import {CategoryInterface, SurveyInterface} from "../interfaces/category_interfaces.ts";
import {languageMap, surveyLanguages} from "../../helpers/languages.ts";


interface EditInterface {
  id: string|null;
  what: string;
}

const defaultPrompt = "The user is uploading a number of files which contains background for a survey.\n" +
  "Create a survey with 3-8 sections.\n" +
  "Each section should represent an area of the survey, and contain 3-8 questions.\n" +
  "Each question should be formulated so that the person answering can " +
  "answer the question with a range from 'Strongly disagree' to 'Strongly agree'.\n" +
  "Start each question with X and then a statement, where X refers to the person the survey is about.\n" +
  "Example question: X communicates clearly."

export default function Survey() {
  const api = Api.getInstance();
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const [language, setLanguage] = useState("");
  const [survey, setSurvey] = useState<SurveyInterface>({
    id: "",
    languages: [],
    translations_name: {},
    translations_answers: {},
    defaultLanguage: "en",
    name: "",
    created: "",
    locked: false,
    approved: false,
    answer_options: 5,
    answer_type: 0,
    show_no_answer: true,
    sections: []
  });
  const [category, setCategory] = useState<CategoryInterface>({id: "", name: "", files: [], surveys: []});
  const [loaded, setLoaded] = useState(false);
  const [showApproveSurvey, setShowApproveSurvey] = useState(false);
  const [doApproveSurvey, setDoApproveSurvey] = useState(false);
  const [editString, setEditString] = useState("");
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [edit, setEdit] = useState<EditInterface | null>(null);

  useEffect(() => {
    getData();
  }, []);

  useEffect(() => {
    if (doApproveSurvey && survey.approved) {
      saveSurvey()
    }
  }, [doApproveSurvey])

  const getData = () => {
    api.get('customer/survey/' + surveyId).then((response) => {
      setSurvey(response.json.survey);
      setCategory(response.json.category);
      setLanguage(response.json.language);
      setLoaded(true);
    })
  }

  const generateSurvey = () => {
    setLoaded(false);
    const data = {
      prompt: prompt
    }
    api.post('customer/survey_ai/' + surveyId, data).then((response) => {
      if (response.json.status === "ok") {
        setSurvey(response.json.survey);
      } else {
        alert(response.json.error);
      }

      setLoaded(true);
    })
  }

  const addLanguage = (lang: string) => {
    setLoaded(false);
    api.post('customer/survey/' + surveyId + '/add_language/' + lang, survey).then((response) => {
      setSurvey(response.json.survey);
      setLanguage(response.json.language);
      setLoaded(true);
    })
  }

  const saveSurvey = () => {
    api.post('customer/survey_save/' + surveyId, survey).then((response) => {
      console.log(response.json.status);
      toast('Your survey was saved');
      console.log('toasted?');
    })
  }

  const countSectionsAndQuestions = () => {
    let sections = 0;
    let questions = 0;
    if (survey) {
      for (let section of survey.sections) {
        sections++;
        questions += section.questions.length;
      }
    }
    return <p className="text-neutral-400">
      {questions} questions across {sections} areas
    </p>
  }

  const lockSurvey = () => {
    setSurvey({...survey, locked: true});
  }

  const approveSurvey = () => {
    setShowApproveSurvey(false);
    setDoApproveSurvey(true);
    setSurvey({...survey, approved: true});
  }

  const addSection = () => {
    const _survey = {...survey};
    _survey.sections.push({
      id: window.crypto.randomUUID(),
      name: editString,
      position: 10,
      created: "",
      translations_name: {},
      questions: [],
    })
    setSurvey(_survey);
    setEditString("");
    setEdit(null);
  }

  const editSection = () => {
    if (!edit) return;
    const _survey = {...survey};
    for (let section of _survey.sections) {
      if (section.id === edit.id) {
        if (language === survey.defaultLanguage) {
          section.name = editString;
        } else {
          section.translations_name[language] = editString;
        }
      }
    }
    setSurvey(_survey);
    setEditString("");
    setEdit(null);
  }

  const editQuestion = () => {
    if (!edit) return;
    const _survey = {...survey};
    for (let section of _survey.sections) {
      for (let _question of section.questions) {
        if (_question.id === edit.id) {
          if (language === survey.defaultLanguage) {
            _question.question = editString;
          } else {
            _question.translations_name[language] = editString;
          }
        }
      }
    }
    setSurvey(_survey);
    setEditString("");
    setEdit(null);
  }

  const editOption = () => {
    if (!edit || !edit.id) return;
    const _survey = {...survey};
    const optionId = parseInt(edit.id);
    _survey.translations_answers[language][optionId] = editString;
    setSurvey(_survey);
    setEditString("");
    setEdit(null);
  }

  const sectionById = (id: string) => {
    for (let section of survey.sections) {
      if (section.id === id) return section;
    }
    return null;
  }

  const questionById = (id: string) => {
    for (let section of survey.sections) {
      for (let question of section.questions) {
        if (question.id === id) {
          return question;
        }
      }
    }
    return null;
  }

  const addQuestion = () => {
    if (!edit || !edit.id) return;
    const section = sectionById(edit.id);
    if (!section) return;
    const _survey = {...survey};
    for (let section of _survey.sections) {
      if (section.id === edit.id) {
        section.questions.push({
          id: window.crypto.randomUUID(),
          question: editString,
          translations_name: {},
          position: 10,
          no_answer: true,
          created: "now",
          free_text: false,
        })
      }
    }
    setSurvey(_survey);
    setEditString("");
    setEdit(null);
  }

  const deleteSection = () => {
    if (!edit || !edit.id) return;
    const _survey = {...survey};
    _survey.sections = _survey.sections.filter(_section => _section.id !== edit.id);
    setSurvey(_survey);
    setEdit(null);
  }

  const deleteQuestion = () => {
    if (!edit || !edit.id) return;
    const _survey = {...survey};
    for (let _section of _survey.sections) {
      _section.questions = _section.questions.filter(_question => _question.id !== edit.id);
    }
    setSurvey(_survey);
    setEdit(null);
  }

  const editSurveyName = () => {
    if (!edit || !edit.id) return;
    const _survey = {...survey};
    if (language === survey.defaultLanguage) {
      _survey.name = editString;
    } else {
        _survey.translations_name[language] = editString
    }

    setSurvey(_survey);
    setEdit(null);
  }

  const handleChange = (orgString: string, cmd: string, changeFunc: Function) => {
    return (
      <div>
        {
          language !== survey.defaultLanguage &&
            <div className="text-neutral-400 text-sm">
                <p>{languageMap[language]}:</p>
            </div>
        }
        <input type="text" className="z-input" value={editString} style={{width: 600, maxWidth: "80vw"}}
               onChange={(e) => setEditString(e.target.value)}/>
        {
          language !== survey.defaultLanguage &&
            <div className="text-neutral-400 text-sm mt-2">
                <p>{languageMap[survey.defaultLanguage]}:</p>
                <p>{orgString}</p>
            </div>
        }
        <div className="mt-4">
          <CustomButton disabled={editString.length < 2}
                        buttonType="outlineWhiteBase" onClick={() => changeFunc()}>
            {cmd}
          </CustomButton>
        </div>
      </div>
    )

  }

  const editModalContent = () => {

    if (!edit) {
      return <div>Huh?</div>
    }
    if (edit.what === "surveyName") {
      return handleChange(survey.name, "Update", editSurveyName);
    } else if (edit.what === "section") {
      if (edit.id) {
        const section = sectionById(edit.id);
        if (!section) {
          return <div>Error: can't find section {edit.id}</div>
        }
        return handleChange(section.name, "Update section", editSection);
      } else {
        return handleChange("", "Add section", addSection);
      }
    } else if (edit.what === "sectionDelete") {
      return (
        <div>
          Are you sure you want to delete this section and all its questions?
          <div className="flex justify-between mt-4 gap-x-4">
            <CustomButton buttonType="outlineWhiteBase" onClick={() => setEdit(null)}>Cancel</CustomButton>
            <CustomButton buttonType="dangerBase" onClick={() => deleteSection()}>Delete</CustomButton>
          </div>
        </div>
      )
    } else if (edit.what === "addQuestion") {
      return handleChange("", "Add question", addQuestion);
    } else if (edit.what === "questionDelete") {
      return (
        <div>
          Are you sure you want to delete this question?
          <div className="flex justify-between mt-4 gap-x-4">
            <CustomButton buttonType="outlineWhiteBase" onClick={() => setEdit(null)}>Cancel</CustomButton>
            <CustomButton buttonType="dangerBase" onClick={() => deleteQuestion()}>Delete</CustomButton>
          </div>
        </div>
      )
    } else if (edit.what === "question") {
      if (edit.id) {
        const _question = questionById(edit.id);
        if (_question) {
          return handleChange(_question.question, "Update question", editQuestion);
        }
      }
      return <>Missing question id</>

      // return (
      //   <div className="flex items-center gap-x-4">
      //     <input type="text" className="z-input" value={editString} placeholder="Question"
      //            onChange={(e) => setEditString(e.target.value)} />
      //     <CustomButton disabled={editString.length < 2}
      //                   buttonType="outlineWhiteBase" onClick={() => editQuestion()}>
      //       Update
      //     </CustomButton>
      //   </div>
      // )
    } else if (edit.what === "option") {
      return (
        <div className="flex items-center gap-x-4">
          <input type="text" className="z-input" value={editString} placeholder="Option"
                 onChange={(e) => setEditString(e.target.value)} />
          <CustomButton buttonType="outlineWhiteBase" onClick={() => editOption()}>
            Update
          </CustomButton>
        </div>
      )
    }
  }

  const toggleQuestionOption = (questionId: string, what: string) => {
    const _survey = {...survey};
    for (let _section of _survey.sections) {
      for (let _question of _section.questions) {
        if (_question.id === questionId) {
          if (what === "free") {
            _question.free_text = !_question.free_text;
          } else if (what === "no_answer") {
            _question.no_answer = !_question.no_answer;
          }
        }
      }
    }
    setSurvey(_survey);
  }

  const showAnswerOptions = () => {

    return (
      <div className="mt-4 z-box">
        Answer options: {survey.answer_options}

        <hr className="mt-2 border-neutral-600"/>

        <div className="mt-2">
          {
            survey.translations_answers[language].map((option, i) => (
              <div key={"o_" + i} className="mt-2 flex items-center">
                {
                  editable() &&
                  <img src={iconPen} className="mr-4 cursor-pointer h-4 w-4" alt="edit" onClick={() => {
                    setEditString(option);
                    setEdit({id: i + "", what: "option"})
                  }}/>
                }
                <p>{i}. {option}</p>
              </div>
            ))
          }
        </div>
      </div>
    )
  }

  const editable = () => {
    if (survey.approved) return false;
    if (survey.locked && language === survey.defaultLanguage) return false;
    return true;
  }

  const showLockApprove = () => {
    if (survey.approved) {
      return (
        <div className="text-neutral-400">Survey is locked and approved</div>
      )
    }
    if (survey.locked) {
      return (
        <>
          <div className="text-neutral-400">Survey is locked</div>
          <Checkbox id="survey_approved" label="Approve survey" className="mt-4"
                    checked={survey.approved} onChange={() => setShowApproveSurvey(true)}/>
        </>
      )
    } else {
      return (
        <Checkbox id="survey_locked" label="Lock survey for translations"
                  checked={survey.locked} onChange={() => lockSurvey()}/>
      )
    }
  }

  const renderMain = () => {
    if (!loaded) {
      return <div>Loading data...</div>
    }
    const crumbs: Array<Crumb> = [
      {
        name: "Categories",
        link: "/categories"
      },
      {
        name: category.name,
        link: "/categories/" + category.id,
      },
      {
        name: "Survey: " + survey.name
      }
    ]
    return (
      <>
        <Breadcrumbs crumbs={crumbs}/>
        <div className="mt-4 flex items-center">
          <div className="text-3xl font-medium">{language === survey.defaultLanguage ? survey.name : survey.translations_name[language]}</div>
          {
            editable() &&
              <img src={iconPen} className="h-6 w-6 cursor-pointer ml-4" alt="edit" onClick={() => {
                setEditString(language === survey.defaultLanguage ? survey.name : survey.translations_name[language]);
                setEdit({id: survey.id, what: "surveyName"})
              }
              }/>
          }

        </div>
        <p className="text-neutral-400">Created: {survey.created.substring(0, 10)}</p>
        <p className="text-neutral-400">Default language: {languageMap[survey.defaultLanguage]}</p>

        <div className="mt-4">
          {
            survey.locked && !survey.approved ? (
              <select className="z-select" value={language}
                      onChange={(e) => addLanguage(e.target.value)}>
                <option value="-">Add language</option>
                {
                  surveyLanguages.filter(l => survey.languages.indexOf(l) < 0).map(l => (
                    <option key={"a_" + l} value={l}>{languageMap[l]}</option>
                  ))
                }
              </select>
            ) : (
              <div className="mt-2 text-neutral-400">Translations can be added when locked</div>
            )
          }

        </div>

        <div className="flex mt-4 gap-x-4">
          {
            survey.languages.map((l, i) => {
              return (
                <Checkbox key={"l_" + i} id={"l_" + i} checked={l === language} label={languageMap[l]}
                          onChange={() => setLanguage(l)}/>
              )
            })
          }
        </div>

        {showAnswerOptions()}

        <div className="mt-4 text-xl font-medium">Generated Questions</div>
        {countSectionsAndQuestions()}

        {
          survey.sections.length < 1 &&
            <div className="mt-4 z-box">
                <p>
                    You can either add questions manually, or let AI create a suggestion based on the category files
                </p>
                <p className="mt-2 text-neutral-400">Edit prompt below until we have found a good one.</p>
                <textarea value={prompt} rows={10} cols={60} className="z-input mt-2"
                          onChange={(e) => setPrompt(e.currentTarget.value)}/>
                <CustomButton buttonType="outlineWhiteBase" className="mt-4" onClick={() => generateSurvey()}
                              leftIcon={<img src={iconAi} alt="ai" className="h-4 w-4"/>}>
                    Generate AI survey
                </CustomButton>
            </div>
        }

        {
          survey.sections.map((section, index) => (
            <div className="mt-4 z-box" key={"section_" + index}>
              <div className="flex justify-between items-center">
                <div className="text-xl font-medium">
                  {language === survey.defaultLanguage ? section.name : section.translations_name[language]}
                </div>
                {
                  editable() &&
                    <div className="flex items-center gap-x-4">
                      <img src={iconPen} className="h-4 w-4 cursor-pointer" alt="edit" onClick={() => {
                        setEditString(language === survey.defaultLanguage ? section.name : section.translations_name[language]);
                        setEdit({id: section.id, what: "section"})
                      }
                      }/>
                      <img src={iconTrash} className="h-4 w-4 cursor-pointer" alt="edit" onClick={() => {
                        setEdit({id: section.id, what: "sectionDelete"})
                      }
                      }/>
                      {
                        !survey.locked &&
                          <CustomButton buttonType="outlineWhiteBase"
                                        onClick={() => setEdit({id: section.id, what: "addQuestion"})}>
                              Add question
                          </CustomButton>
                      }
                    </div>
                }

              </div>

              {
                section.questions.map(question => (
                  <div className="mt-4 p-4 border border-neutral-600 rounded-[6px]" key={"question_" + question.id}>
                    <div className="flex justify-between">
                      <div className="text-neutral-400">
                        {language === survey.defaultLanguage ? question.question : question.translations_name[language]}
                      </div>
                      {
                        editable() ? (
                          <div className="flex items-center gap-x-4">
                            <img src={iconPen} className="h-4 w-4 cursor-pointer" alt="edit" onClick={() => {
                              setEditString(language === survey.defaultLanguage ? question.question : question.translations_name[language]);
                              setEdit({id: question.id, what: "question"})
                            }}/>
                            <img src={iconTrash} className="h-4 w-4 cursor-pointer" alt="edit" onClick={() => {
                              setEdit({id: question.id, what: "questionDelete"})
                            }}/>
                          </div>
                        ) : (
                          <img src={iconLocked} alt="locked" className="h-4 w-4" />
                        )
                      }

                    </div>

                    {
                      editable() &&
                        <div className="flex justify-between items-center mt-4">
                          <div className="flex">
                            <div>
                                <Checkbox id={"free_text_" + question.id} checked={question.free_text}
                                          onChange={(checked) => toggleQuestionOption(question.id, "free")}
                                          label="Free text answer"/>
                            </div>
                          </div>
                        </div>
                    }
                  </div>
                ))
              }
            </div>
          ))
        }

        {
          !survey.locked &&
            <div className="mt-8 text-center">
              <CustomButton buttonType="blueBase" onClick={() => setEdit({id: null, what: "section"})}>
                  Add Area
              </CustomButton>
            </div>
        }

        {
          survey.sections.length > 0 &&
            <div className="mt-8">
              {showLockApprove()}

              {
                !survey.approved &&
                  <CustomButton className="mt-4" buttonType="outlineWhiteBase" onClick={() => saveSurvey()}>
                      Save survey
                  </CustomButton>
              }
            </div>
        }
        {
          survey.approved &&
          <div className="mt-8 underline cursor-pointer"
               onClick={() => navigate('/categories/survey/' + survey.id + '/requests')}>
              See survey feedback requests
          </div>
        }

      </>
    )
  }

  return (
    <div className="main">
      {renderMain()}

      <CustomModal
        isOpen={edit ? true : false}
        onRequestClose={() => setEdit(null)}
      >
        {editModalContent()}
      </CustomModal>
      <CustomModal
        isOpen={showApproveSurvey}
        onRequestClose={() => setShowApproveSurvey(false)}
      >
        <p>
          Once you have approved the survey, you cannot make changes.
        </p>
        <p className="mt-2">
          Make sure you have double checked all texts.
        </p>

        <div className="flex items-center gap-x-4 mt-8">
          <CustomButton buttonType="outlineWhiteBase" onClick={() => setShowApproveSurvey(false)}>
            Cancel
          </CustomButton>
          <CustomButton buttonType="blueBase" onClick={() => approveSurvey()}>
            Approve
          </CustomButton>
        </div>
      </CustomModal>
      <ToastContainer position="top-center" autoClose={1200}/>
    </div>
  );
}